import { toast } from "react-toastify";

// image file types
export const imageFileTypes = "image/jpeg, image/png, image/jpg";

// pdf file types
export const pdfFileTypes = "application/pdf";

// excel file types
export const excelFileTypes =
  "application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// zip file types
export const zipFileTypes =
  "application/zip, application/x-zip-compressed, application/x-tar, application/x-gtar";

const toastOptions = {
  position: "top-right" as const,
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
};

/**
 * Checks if a file's actual content matches the expected MIME type
 * by reading the file's signature (magic bytes)
 */
export const validateFileContent = async (
  file: File,
  expectedTypes: string[]
): Promise<boolean> => {
  // Read the first few bytes of the file to check its signature
  const buffer = await readFileAsArrayBuffer(file, 0, 12);
  const bytes = new Uint8Array(buffer);

  // Common file signatures (magic bytes)
  const signatures: Record<string, number[][]> = {
    // Images
    "image/jpeg": [[0xff, 0xd8, 0xff]],
    "image/png": [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],

    // PDFs
    "application/pdf": [[0x25, 0x50, 0x44, 0x46]], // %PDF

    // Excel
    "application/vnd.ms-excel": [
      [0xd0, 0xcf, 0x11, 0xe0], // OLE2 binary XLS (Excel 97-2003)
      [0x3c, 0x3f, 0x78, 0x6d], // <?xm - XML Spreadsheet 2003
      [0xef, 0xbb, 0xbf, 0x3c], // UTF-8 BOM + < - XML with BOM
      [0x3c, 0x68, 0x74, 0x6d], // <htm - HTML saved as .xls
      [0x3c, 0x48, 0x54, 0x4d], // <HTM - HTML (uppercase) saved as .xls
    ],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      [0x50, 0x4b, 0x03, 0x04], // xlsx (ZIP structure)
    ],

    // Zip/Tar
    "application/zip": [[0x50, 0x4b, 0x03, 0x04]],
    "application/x-zip-compressed": [[0x50, 0x4b, 0x03, 0x04]],
    "application/x-tar": [[0x75, 0x73, 0x74, 0x61, 0x72]], // ustar
  };

  // Check if the file's bytes match any of the expected signatures
  for (const expectedType of expectedTypes) {
    const validSignatures = signatures[expectedType];
    if (!validSignatures) continue;

    for (const signature of validSignatures) {
      let isMatch = true;
      for (let i = 0; i < signature.length; i++) {
        if (bytes[i] !== signature[i]) {
          isMatch = false;
          break;
        }
      }
      if (isMatch) return true;
    }
  }

  return false;
};

/**
 * Helper function to read a portion of a file as ArrayBuffer
 */
const readFileAsArrayBuffer = (
  file: File,
  start: number = 0,
  length: number = 4
): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(start, start + length));
  });
};

// Common validation logic
const validateFile = async (
  file: File,
  validFileTypes: string[],
  maxFileSize: number,
  fileTypeWarningMessage: string
) => {
  // Check if the file type is valid
  const isFileTypeValid = validFileTypes?.some((fileType) =>
    fileType.endsWith(file.type)
  );
  if (!isFileTypeValid) {
    toast.warning(fileTypeWarningMessage, toastOptions);
    return false;
  }

  // Check if the file size exceeds the limit
  if (file.size > maxFileSize) {
    toast.warning(
      `File size exceeds ${maxFileSize / 1000000} MB`,
      toastOptions
    );
    return false;
  }

  // Validate the actual file content matches the expected type

  if (
    validFileTypes?.includes("application/zip") ||
    validFileTypes?.includes("application/x-zip-compressed") ||
    validFileTypes?.includes("application/x-tar") ||
    validFileTypes?.includes("application/x-gtar")
  ) {
    return true;
  }

  const isContentValid = await validateFileContent(file, validFileTypes);

  if (!isContentValid) {
    toast.warning(`File appears to be invalid or corrupted`, toastOptions);
    return false;
  }

  // File is valid
  return true;
};

export const validateImageFile = async (
  file: File,
  validateDimensions: boolean = false, // Default is false
  requiredWidth: number = 1280, // Default width
  requiredHeight: number = 720 // Default height
): Promise<boolean> => {
  const validFileTypes = imageFileTypes.split(",").map((type) => type.trim());
  const maxFileSize = 25000000; // 25 MB
  const fileTypeWarningMessage =
    "Please upload a valid image file (jpg, jpeg, png)";

  // Validate file type and size
  const isValidFile = await validateFile(
    file,
    validFileTypes,
    maxFileSize,
    fileTypeWarningMessage
  );
  if (!isValidFile) {
    return false;
  }

  // If dimension validation is not required, return true
  if (!validateDimensions) {
    return true;
  }

  // Validate image dimensions
  const image = new Image();
  const imageUrl = URL.createObjectURL(file);

  return new Promise((resolve) => {
    image.onload = () => {
      if (image.width === requiredWidth && image.height === requiredHeight) {
        resolve(true); // Dimensions are valid
      } else {
        toast.warning(
          `Image dimensions must be ${requiredWidth}x${requiredHeight} pixels`,
          toastOptions
        );
        resolve(false); // Dimensions are invalid
      }
      URL.revokeObjectURL(imageUrl); // Clean up the object URL
    };

    image.onerror = () => {
      toast.warning("Unable to validate image dimensions", toastOptions);
      resolve(false); // Return false if the image cannot be loaded
    };

    image.src = imageUrl; // Set the image source
  });
};

// Validation for PDF files
export const validatePdfFile = async (file: File) => {
  const validFileTypes = pdfFileTypes.split(",").map((type) => type.trim());
  const maxFileSize = 5000000; // 5 MB
  const fileTypeWarningMessage = "Please upload a valid PDF file";
  return await validateFile(
    file,
    validFileTypes,
    maxFileSize,
    fileTypeWarningMessage
  );
};

// Validation for Excel files
export const validateExcelFile = async (file: File) => {
  const validFileTypes = excelFileTypes.split(",").map((type) => type.trim());
  const maxFileSize = 30000000; // 30 MB
  const fileTypeWarningMessage = "Please upload a valid Excel file (.xlsx or .xls)";
  return await validateFile(
    file,
    validFileTypes,
    maxFileSize,
    fileTypeWarningMessage
  );
};

// Validation for ZIP/TAR files
export const validateZipOrTarFile = async (file: File) => {
  const validFileTypes = zipFileTypes.split(",").map((type) => type.trim());
  const maxFileSize = 30000000; // 30 MB
  const fileTypeWarningMessage = "Please upload a valid ZIP or TAR file";

  return await validateFile(
    file,
    validFileTypes,
    maxFileSize,
    fileTypeWarningMessage
  );
};